import { parseISO } from 'date-fns';
import type { ChannelTag } from './channels-tag';
import { getTagData } from './channels-tag';
import type { Image } from './image';
import type { MediaDataObject, VideoDetailDataObject, VideoListItemDataObject } from './service/video';

export type VideoListItem = {
  coverPhoto?: Image;
  id: string;
  name: string;
  recordedAt: Date;
  status: VideoStatus;
  trailer?: {
    externalId: string;
  };
};

export const createVideoListItem = (data: VideoListItemDataObject): VideoListItem => ({
  coverPhoto: data.cover_photo,
  id: data.id,
  name: data.name,
  recordedAt: parseISO(data.recorded_at),
  status: data.status,
  trailer: data.trailer ? { externalId: data.trailer.external_id } : undefined,
});

export type VideoDetail = {
  createdAt: Date;
  featuredInChannels: boolean;
  full?: { externalId: string };
  htmlContent: string;
  price: unknown;
  priceOption: 'free' | 'paid';
  showInChannels: boolean;

  tags: ChannelTag[];
  textContent: string;
  updatedAt: Date;
} & VideoListItem;

export type VideoStatus = 'archived' | 'draft' | 'published';

type MediaStream =
  | {
      meta: {
        storageType: string;
      };
      path: string;
      status: 'active';
    }
  | {
      status: 'processing';
    };

export const createVideoDetail = (data: VideoDetailDataObject): VideoDetail => ({
  ...createVideoListItem(data),
  createdAt: parseISO(data.created_at),
  featuredInChannels: data.featured_in_channels,
  full: data.full ? { externalId: data.full.external_id } : undefined,
  htmlContent: data.html_content,
  price: data.price,
  priceOption: data.price_option,
  showInChannels: data.show_in_channels,
  status: data.status,
  tags: data.tags.map((tag) => getTagData(tag)),
  textContent: data.text_content,
  updatedAt: parseISO(data.updated_at),
});

export type Media = {
  alts?: {
    stream?: MediaStream;
  };
  id: string;
  meta: {
    duration?: number;
  };
  name: string;
  size: number;
};

export const createMediaObject = (data: MediaDataObject): Media => ({
  alts: data.alts,
  id: data._id,
  meta: data.meta,
  name: data.name,
  size: data.size,
});
